import { getPartnerToken } from '@/services/auth/partnerSessionStorage';
import {
  CONNECTIVITY_MODES,
  connectivityStatus,
} from '@/services/connectivity/connectivityService';
import { getLocalHubCredential } from './hubCredentialStore';
import { postToHub } from './hubPairingService';
import { waitForCloudRequestsToSettle } from '@/services/http/deviceHeaderInterceptor';
import { waitForLocalRequests } from './localModeService';
import { saveEmergencyHubProfile } from './hubDiscoveryService';

const LEASE_KEY = 'ewu_local_control_leader';
const LEASE_MS = 15000;
const TICK_MS = 5000;
const REPORT_MS = 10000;
const CHANNEL_NAME = 'ewu-local-control';

const tabId = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

let activeStore = null;
let activeDiscovery = null;
let socket = null;
let timer = null;
let channel = null;
let lastReportAt = 0;
let reconnectAt = 0;
let failures = 0;
let tickRequest = null;
let lastSocketMessageAt = 0;

const STALE_SOCKET_TIMEOUT_MS = 30000;
const HUB_FAILURE_THRESHOLD = 3;
const intentionallyClosedSockets = new WeakSet();

function readToken() {
  const value = activeStore?.state?.partner_token || getPartnerToken();
  return value && value !== 'null' && value !== 'undefined' ? value : null;
}

function readLease() {
  try { return JSON.parse(localStorage.getItem(LEASE_KEY) || 'null'); } catch { return null; }
}

function isLeader() {
  const now = Date.now();
  const lease = readLease();
  if (!lease || lease.tabId === tabId || Number(lease.expiresAt) <= now) {
    try {
      localStorage.setItem(LEASE_KEY, JSON.stringify({ tabId, expiresAt: now + LEASE_MS }));
    } catch { /* A single tab can still operate without storage. */ }
    return true;
  }
  return false;
}

function releaseLease() {
  try {
    if (readLease()?.tabId === tabId) localStorage.removeItem(LEASE_KEY);
  } catch { /* Lease expires. */ }
}

function broadcast(payload) {
  try { channel?.postMessage({ type: 'control-state', sender: tabId, payload }); } catch { /* Optional. */ }
}

function broadcastOperationalEvent(payload) {
  try { channel?.postMessage({ type: 'operational-event', sender: tabId, payload }); } catch { /* Optional. */ }
}

function commitState(payload) {
  activeStore?.commit('device/setLocalControlState', payload);
  broadcast(payload);
}

function cloudConfirmsCloudMode() {
  const device = activeStore?.state?.device;
  return (
    connectivityStatus.mode === CONNECTIVITY_MODES.ONLINE
    && device?.operationalAccessLoaded === true
    && device?.restaurantOperationMode === 'CLOUD_MODE'
  );
}

function applyHubControlContext(payload) {
  const hubReportsLocal = ['LOCAL_MODE', 'LOCAL_MODE_PREPARING'].includes(
    payload?.operation_mode,
  );
  if (hubReportsLocal && cloudConfirmsCloudMode()) {
    activeStore?.commit('operationalMode/setCloudAuthority', {
      operation_epoch: Number(
        activeStore?.state?.device?.operationEpoch
        || payload?.operation_epoch
        || 1,
      ),
      hub_reachable: true,
    });
    return false;
  }
  activeStore?.commit('operationalMode/setContext', {
    operation_mode: payload.operation_mode,
    operation_epoch: payload.operation_epoch,
    session: payload.local_mode_session,
    connected_devices: payload.connected_local_devices,
    recovery: payload.recovery,
  });
  return true;
}

function cloudReport(type = 'device.cloud_reachability') {
  let cloudStatus = 'UNKNOWN';
  if (connectivityStatus.mode === CONNECTIVITY_MODES.ONLINE) {
    cloudStatus = readToken() ? 'API_REACHABLE_AUTHENTICATED' : 'API_REACHABLE_UNAUTHENTICATED';
  } else if (connectivityStatus.mode === CONNECTIVITY_MODES.BROWSER_OFFLINE) {
    cloudStatus = 'NETWORK_OFFLINE';
  } else if (connectivityStatus.mode === CONNECTIVITY_MODES.BACKEND_UNAVAILABLE) {
    cloudStatus = 'API_UNREACHABLE';
  }
  return {
    type,
    protocol_version: 1,
    frontend_version: process.env.VUE_APP_VERSION || '0.1.0',
    cloud_status: cloudStatus,
    cloud_latency_ms: null,
    cloud_websocket_connected: false,
    observed_at: new Date().toISOString(),
    last_cloud_success_at: cloudStatus === 'API_REACHABLE_AUTHENTICATED'
      ? new Date().toISOString()
      : null,
  };
}

function closeSocket() {
  const current = socket;
  socket = null;
  if (current) intentionallyClosedSockets.add(current);
  try { current?.close(1000, 'stopped'); } catch { /* Already closed. */ }
}

function markHubUnavailableAfterRepeatedFailures() {
  if (failures >= HUB_FAILURE_THRESHOLD) {
    activeStore?.commit('operationalMode/setHubUnavailable');
  }
}

async function openControlSocket() {
  if (!activeDiscovery?.url || !activeDiscovery?.profile?.hub_uuid || socket) return;
  const credential = await getLocalHubCredential(activeDiscovery.profile.hub_uuid);
  if (!credential) return;
  const response = await postToHub(
    `${activeDiscovery.url}/local/v1/sessions`,
    {
      local_device_id: credential.local_device_id,
      local_device_secret: credential.local_device_secret,
    },
    4000,
  );
  const candidate = new WebSocket(
    response.websocket_url,
    ['ewu-control-v1', response.session_token],
  );
  socket = candidate;
  candidate.onopen = () => {
    failures = 0;
    reconnectAt = 0;
    lastReportAt = Date.now();
    lastSocketMessageAt = Date.now();
    candidate.send(JSON.stringify(cloudReport('device.hello')));
    commitState({
      connected: true,
      paired: true,
      last_connected_at: new Date().toISOString(),
      last_error: null,
    });
    activeStore?.commit('operationalMode/setHubAvailable');
  };
  candidate.onmessage = (event) => {
    lastSocketMessageAt = Date.now();
    try {
      const payload = JSON.parse(event.data);
      if (payload.type === 'hub.control_state') {
        const wasConnected = Boolean(
          activeStore?.state?.device?.localControlState?.connected,
        );
        commitState({ connected: true, paired: true, ...payload });
        if (payload.network) {
          activeStore?.commit('emergencyNetwork/setStatus', payload.network);
        }
        const previousMode = activeStore?.state?.operationalMode?.mode;
        const acceptedHubAuthority = applyHubControlContext(payload);
        activeStore?.commit(
          'operationalMode/setCloudReachable',
          payload.hub_cloud_reachable,
        );
        if (
          acceptedHubAuthority
          && payload.operation_mode === 'LOCAL_MODE'
          && (previousMode !== 'LOCAL_MODE' || !wasConnected)
        ) {
          void activeStore?.dispatch('operationalMode/loadBootstrap')
            .catch(() => {});
          void activeStore?.dispatch('operationalMode/recoverEvents')
            .catch(() => {});
        }
      } else if (payload.type === 'local.domain_event') {
        if (activeStore?.state?.operationalMode?.mode === 'LOCAL_MODE') {
          activeStore?.commit('operationalMode/appendEvent', payload);
        } else {
          activeStore?.commit('localRehearsal/appendEvent', payload);
        }
        broadcastOperationalEvent(payload);
      } else if (String(payload.type || '').startsWith('network.')) {
        activeStore?.commit('emergencyNetwork/applyEvent', payload);
        const endpoint = payload.hub_endpoint;
        if (endpoint && activeDiscovery?.profile) {
          void saveEmergencyHubProfile(
            activeDiscovery.profile,
            {
              network_mode: 'EMERGENCY_NETWORK',
              ssid: payload.ssid,
              hub_endpoint: endpoint,
              certificate_fingerprint: payload.certificate_fingerprint,
              operation_epoch: payload.operation_epoch,
              session_uuid: payload.session_uuid,
            },
          ).then((profile) => {
            activeDiscovery = { ...activeDiscovery, profile, url: endpoint };
            closeSocket();
            reconnectAt = 0;
          }).catch(() => {});
        }
        broadcastOperationalEvent(payload);
      } else if (payload.type === 'local_mode.prepare') {
        activeStore?.commit('operationalMode/setPreparing', payload);
        void waitForCloudRequestsToSettle(8000).then((settled) => (
          // Acknowledge through the already authenticated control channel.
          // Going back through fetch here can be blocked by CORS or a stalled
          // cloud request exactly while the Hub is preparing failover.
          candidate.readyState === WebSocket.OPEN
            && candidate.send(JSON.stringify({
              type: 'local_mode.prepare_ack',
              transition_id: payload.transition_id,
              ready: settled,
              cloud_reachable: cloudReport().cloud_status
                === 'API_REACHABLE_AUTHENTICATED',
              last_cloud_request_completed_at: new Date().toISOString(),
              last_known_cloud_sequence: Number(payload.base_cloud_sequence || 0),
              local_protocol_version: 1,
              reason: settled ? null : 'CLOUD_REQUESTS_IN_FLIGHT',
            }))
        )).catch(() => {});
        broadcastOperationalEvent(payload);
      } else if (payload.type === 'local_mode.commit') {
        activeStore?.commit('operationalMode/setCommitted', payload);
        void activeStore?.dispatch('operationalMode/loadBootstrap')
          .catch(() => {});
        broadcastOperationalEvent(payload);
      } else if (payload.type === 'cloud_return.prepare') {
        activeStore?.commit('operationalMode/setCloudReturnPreparing', payload);
        void waitForLocalRequests(8000).then((settled) => (
          activeStore?.dispatch('operationalMode/acknowledgeReturn', {
            ...payload,
            readiness: {
              reason: settled ? null : 'LOCAL_REQUESTS_IN_FLIGHT',
            },
          })
        )).catch(() => {});
        broadcastOperationalEvent(payload);
      } else if (payload.type === 'cloud_return.commit') {
        activeStore?.commit('operationalMode/setCloudReturned', payload);
        void activeStore?.dispatch('device/refreshOperationalStatus')
          .catch(() => {});
        broadcastOperationalEvent(payload);
      } else if (payload.type === 'hub.device_revoked') {
        commitState({ connected: false, paired: false, revoked: true });
        closeSocket();
      } else if (payload.type === 'hub.protocol_incompatible') {
        commitState({
          connected: false,
          last_error: { code: 'CONTROL_PROTOCOL_INCOMPATIBLE' },
        });
        closeSocket();
      }
    } catch {
      commitState({ last_error: { code: 'CONTROL_MESSAGE_INVALID' } });
    }
  };
  candidate.onerror = () => {
    commitState({
      connected: false,
      last_error: { code: 'LOCAL_CONTROL_SOCKET_ERROR' },
    });
  };
  candidate.onclose = () => {
    if (intentionallyClosedSockets.has(candidate)) {
      commitState({ connected: false });
      return;
    }
    if (socket === candidate) socket = null;
    failures += 1;
    reconnectAt = Date.now() + Math.min(60000, (2 ** Math.min(failures, 5)) * 1000);
    commitState({ connected: false });
    markHubUnavailableAfterRepeatedFailures();
  };
}

function tick() {
  if (tickRequest) return tickRequest;
  tickRequest = performTick().finally(() => {
    tickRequest = null;
  });
  return tickRequest;
}

async function performTick() {
  if (!readToken()) {
    closeSocket();
    releaseLease();
    return;
  }
  if (!isLeader()) {
    closeSocket();
    return;
  }
  if (!socket && Date.now() >= reconnectAt) {
    try {
      await openControlSocket();
    } catch (error) {
      failures += 1;
      reconnectAt = Date.now() + Math.min(60000, (2 ** Math.min(failures, 5)) * 1000);
      commitState({
        connected: false,
        last_error: {
          code: error?.response?.data?.detail || 'LOCAL_CONTROL_CONNECT_FAILED',
          message: error?.message,
        },
      });
      markHubUnavailableAfterRepeatedFailures();
    }
  }
  if (
    socket?.readyState === WebSocket.OPEN
    && lastSocketMessageAt
    && Date.now() - lastSocketMessageAt >= STALE_SOCKET_TIMEOUT_MS
  ) {
    // A half-open WebSocket can survive a browser/network transition while
    // no longer carrying reports. Reconnect it so the Hub can receive a
    // fresh cloud-reachability report and complete automatic failover.
    closeSocket();
    reconnectAt = Date.now() + 1000;
    return;
  }
  if (
    socket?.readyState === WebSocket.OPEN
    && Date.now() - lastReportAt >= REPORT_MS
  ) {
    lastReportAt = Date.now();
    socket.send(JSON.stringify(cloudReport()));
  }
}

export async function startHubControlChannel(store, discovery) {
  activeStore = store || activeStore;
  activeDiscovery = discovery || activeDiscovery;
  if (!activeStore || !activeDiscovery) return false;
  if (!channel && typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event) => {
      if (event.data?.sender !== tabId && event.data?.type === 'control-state') {
        activeStore?.commit('device/setLocalControlState', event.data.payload);
      } else if (
        event.data?.sender !== tabId
        && event.data?.type === 'operational-event'
      ) {
        if (event.data.payload?.type === 'local_mode.prepare') {
          activeStore?.commit('operationalMode/setPreparing', event.data.payload);
        } else if (event.data.payload?.type === 'local_mode.commit') {
          activeStore?.commit('operationalMode/setCommitted', event.data.payload);
        } else if (event.data.payload?.type === 'cloud_return.prepare') {
          activeStore?.commit(
            'operationalMode/setCloudReturnPreparing', event.data.payload,
          );
        } else if (event.data.payload?.type === 'cloud_return.commit') {
          activeStore?.commit('operationalMode/setCloudReturned', event.data.payload);
        } else if (
          String(event.data.payload?.type || '').startsWith('network.')
        ) {
          activeStore?.commit(
            'emergencyNetwork/applyEvent', event.data.payload,
          );
        } else if (activeStore?.state?.operationalMode?.mode === 'LOCAL_MODE') {
          activeStore?.commit('operationalMode/appendEvent', event.data.payload);
        } else {
          activeStore?.commit('localRehearsal/appendEvent', event.data.payload);
        }
      }
    };
  }
  if (!timer) timer = window.setInterval(() => { void tick(); }, TICK_MS);
  await tick();
  return true;
}

export function stopHubControlChannel() {
  if (timer) window.clearInterval(timer);
  timer = null;
  closeSocket();
  releaseLease();
  try { channel?.close(); } catch { /* Optional. */ }
  channel = null;
  activeDiscovery = null;
  activeStore?.commit('device/setLocalControlState', { connected: false });
}
